/**
 * Date/Time Utilities for BayesPrep
 * 
 * ARCHITECTURE:
 * - Backend: Stores all timestamps in UTC (or treats LocalDateTime as UTC)
 * - Transport: ISO 8601 strings (e.g., "2025-10-20T09:30:00" or "2025-10-20T09:30:00.000Z")
 * - Frontend: Parse as UTC, display in user's local timezone
 * 
 * RULES:
 * 1. All dates from backend are assumed to be in UTC
 * 2. All dates sent to backend must be in UTC
 * 3. Only convert to local timezone when displaying to user
 * 4. Always show timezone info to avoid confusion
 */

/**
 * Parse timestamp from backend (assumes UTC)
 * Handles both ISO strings with/without 'Z' suffix
 * 
 * @param dateString - Timestamp from backend
 * @returns Date object
 */
function parseBackendDate(dateString: string): Date {
  // If already has 'Z' or timezone offset, use directly
  if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
    return new Date(dateString);
  }
  
  // Backend sends LocalDateTime without timezone - treat as UTC
  // Example: "2025-10-20T15:00:00" → "2025-10-20T15:00:00Z"
  return new Date(`${dateString}Z`);
}
export function formatToLocalTime(
  dateString: string | undefined | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return 'N/A';

  try {
    // Parse as UTC timestamp
    const date = parseBackendDate(dateString);
    
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    };

    return date.toLocaleString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

/**
 * Format just the time portion
 */
export function formatToLocalTimeOnly(dateString: string | undefined | null): string {
  return formatToLocalTime(dateString, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format just the date portion
 */
export function formatToLocalDateOnly(dateString: string | undefined | null): string {
  return formatToLocalTime(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format as "Oct 20, 2025 at 5:12 PM"
 */
export function formatToFriendlyDateTime(dateString: string | undefined | null): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const datePart = formatToLocalDateOnly(dateString);
    const timePart = formatToLocalTimeOnly(dateString);
    return `${datePart} at ${timePart}`;
  } catch (error) {
    return 'Invalid Date';
  }
}

/**
 * Get relative time (e.g., "2 hours ago", "in 30 minutes")
 */
export function getRelativeTime(dateString: string | undefined | null): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffSec = Math.floor(Math.abs(diffMs) / 1000);
    const isPast = diffMs < 0;

    if (diffSec < 60) {
      return isPast ? 'Just now' : 'In a moment';
    }

    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) {
      return isPast ? `${diffMin} min ago` : `in ${diffMin} min`;
    }

    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) {
      return isPast ? `${diffHour} hour${diffHour > 1 ? 's' : ''} ago` : `in ${diffHour} hour${diffHour > 1 ? 's' : ''}`;
    }

    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 30) {
      return isPast ? `${diffDay} day${diffDay > 1 ? 's' : ''} ago` : `in ${diffDay} day${diffDay > 1 ? 's' : ''}`;
    }

    return formatToLocalDateOnly(dateString);
  } catch (error) {
    return 'Invalid Date';
  }
}

/**
 * Get user's current timezone abbreviation
 * @returns Timezone abbreviation (e.g., "IST", "PST", "GMT")
 */
export function getUserTimezone(): string {
  const date = new Date();
  const tzString = date.toLocaleString('en-US', { timeZoneName: 'short' });
  const match = tzString.match(/\b([A-Z]{2,5})\b$/);
  return match ? match[1] : 'Local';
}

/**
 * Get user's timezone offset from UTC
 * @returns Offset string (e.g., "UTC+5:30", "UTC-8:00")
 */
export function getUserTimezoneOffset(): string {
  const offset = -new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Format timestamp with timezone info
 * @param dateString - Timestamp from backend
 * @returns Full formatted string with timezone (e.g., "Oct 20, 2025, 3:00 PM IST")
 */
export function formatWithTimezone(dateString: string | undefined | null): string {
  if (!dateString) return 'N/A';
  
  try {
    const date = parseBackendDate(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  } catch (error) {
    return 'Invalid Date';
  }
}

/**
 * Get seconds difference between timestamp and now
 * Useful for countdown timers
 * 
 * @param dateString - Timestamp from backend
 * @returns Seconds until/since the timestamp (negative if in past)
 */
export function getSecondsDiff(dateString: string): number {
  const targetDate = parseBackendDate(dateString);
  const now = new Date();
  return Math.floor((targetDate.getTime() - now.getTime()) / 1000);
}

/**
 * Convert local Date to UTC ISO string for backend
 * Use this when sending timestamps to the backend
 * 
 * @param localDate - Date object in browser timezone
 * @returns UTC ISO string (e.g., "2025-10-20T09:30:00.000Z")
 */
export function toUTC(localDate: Date): string {
  return localDate.toISOString();
}

/**
 * Debug helper: Log timestamp in multiple formats
 * Useful for debugging timezone issues
 * 
 * @param dateString - Timestamp string to debug
 * @param label - Optional label for the log
 */
export function debugTimestamp(dateString: string, label: string = 'Timestamp'): void {
  try {
    const date = parseBackendDate(dateString);
    console.log(`🕐 ${label} Debug:`, {
      input: dateString,
      utc: date.toISOString(),
      local: date.toLocaleString(),
      timezone: getUserTimezone(),
      offset: getUserTimezoneOffset(),
      timestamp: date.getTime()
    });
  } catch (error) {
    console.error(`❌ Error debugging timestamp "${dateString}":`, error);
  }
}
